import { motion } from "framer-motion";
import { Phone, MapPin, Clock, AlertCircle } from "lucide-react";

const AREAS = [
  "Toronto",
  "Mississauga",
  "Vaughan",
  "Brampton",
  "Markham",
  "Richmond Hill",
  "North York",
  "Etobicoke",
  "Scarborough",
  "Oakville",
  "Pickering",
  "Ajax",
];

// OpenStreetMap embed centered on Toronto (no API key required)
const MAP_SRC =
  "https://www.openstreetmap.org/export/embed.html?bbox=-79.8500%2C43.5800%2C-79.1500%2C43.9200&layer=mapnik&marker=43.6532%2C-79.3832";

export default function ServiceArea() {
  return (
    <section id="service-area" data-testid="service-area-section" className="pc-section bg-white">
      <div className="pc-container">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <div className="eyebrow text-[var(--pc-green-hover)]">Service Area</div>
            <h2 className="font-display mt-3 max-w-2xl text-4xl font-black tracking-tighter sm:text-5xl">
              Toronto & the GTA — we cover the map.
            </h2>
          </div>
          <p className="max-w-md text-base text-black/60">
            From downtown condos to suburban estates, our crews show up across
            the Greater Toronto Area, six days a week.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          {/* Map */}
          <div className="lg:col-span-7">
            <div
              data-testid="service-area-map"
              className="relative overflow-hidden rounded-3xl border border-black/10 bg-[var(--pc-grey)]"
            >
              <iframe
                title="Pristyn Cleaning Service Area"
                src={MAP_SRC}
                className="h-[420px] w-full md:h-[520px]"
                style={{ border: 0, filter: "grayscale(0.3) contrast(1.05)" }}
                loading="lazy"
              />
              <div className="pointer-events-none absolute left-5 top-5 rounded-full bg-black px-4 py-2 text-xs uppercase tracking-[0.25em] text-[var(--pc-green)]">
                <span className="inline-flex items-center gap-2">
                  <MapPin size={14} /> GTA Coverage
                </span>
              </div>
            </div>
          </div>

          {/* Areas + Info */}
          <div className="flex flex-col gap-5 lg:col-span-5">
            <div className="rounded-3xl border border-black/10 bg-white p-6 md:p-8">
              <div className="eyebrow text-black/40">Cities we clean</div>
              <ul className="mt-4 flex flex-wrap gap-2">
                {AREAS.map((a, i) => (
                  <motion.li
                    key={a}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: i * 0.03 }}
                    className="rounded-full border border-black/10 bg-[var(--pc-grey)] px-4 py-2 text-sm font-medium"
                    data-testid={`area-chip-${a.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {a}
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Hours card */}
            <div className="rounded-3xl bg-black p-6 text-white md:p-7">
              <div className="flex items-start gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--pc-green)] text-black">
                  <Clock size={18} />
                </span>
                <div>
                  <div className="eyebrow text-[var(--pc-green)]">Hours</div>
                  <div className="mt-2 text-lg font-semibold">
                    Mon – Sat · 8:00 AM – 7:00 PM
                  </div>
                  <div className="mt-1 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs uppercase tracking-[0.2em]">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-400" /> Closed Sundays
                  </div>
                </div>
              </div>
            </div>

            {/* Not sure box */}
            <div
              data-testid="not-sure-box"
              className="group relative overflow-hidden rounded-3xl border border-[var(--pc-green)] bg-[var(--pc-green-light)] p-6 md:p-7"
            >
              <div className="flex items-start gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-black text-[var(--pc-green)]">
                  <AlertCircle size={18} />
                </span>
                <div className="flex-1">
                  <div className="eyebrow text-black/50">Outside the map?</div>
                  <h3 className="font-display mt-2 text-xl font-bold tracking-tight md:text-2xl">
                    Not sure if we serve your area?
                  </h3>
                  <p className="mt-2 text-sm text-black/70">
                    Give us a quick call — we cover more of the GTA than the
                    map shows, and we're happy to make it work.
                  </p>
                  <a
                    href="tel:6476099266"
                    data-testid="not-sure-call-button"
                    className="pc-btn-primary mt-5 text-sm"
                  >
                    <Phone size={16} /> Call 647-609-9266
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
